/**
 * Keep the app sized to the VISUAL viewport while an on-screen keyboard is up.
 *
 * Layout shells use Tailwind `h-screen`, which index.css redefines as
 * `height: var(--app-height, 100dvh)`. Browsers that only resize the visual
 * viewport when the keyboard opens (the Tesla browser, mobile browsers in
 * `resizes-visual` mode) leave the layout viewport — and with it any
 * viewport-unit height — at full size, so the bottom half of the app
 * (including the message composer) ends up hidden behind the keyboard.
 *
 * This sync writes the visual viewport height into `--app-height` whenever it
 * diverges from the layout viewport by more than the threshold (keyboard
 * open) and clears it when they re-converge. Browsers that already resize the
 * layout viewport natively (`interactive-widget=resizes-content` in the
 * viewport meta, honored by newer Chromium) never diverge, so the var stays
 * unset and CSS keeps full authority there.
 */

/** Divergence below this is browser chrome (URL bar) noise, not a keyboard. */
const KEYBOARD_THRESHOLD_PX = 80;

export function initVisualViewportSync(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  const root = document.documentElement;
  const sync = () => {
    const keyboardOpen = window.innerHeight - vv.height > KEYBOARD_THRESHOLD_PX;
    if (keyboardOpen) {
      root.style.setProperty('--app-height', `${Math.round(vv.height)}px`);
      // The browser pans the layout viewport to reveal the focused input;
      // with the app shrunk to the visual viewport that pan would just push
      // the header off-screen — undo the layout-scroll component of it.
      window.scrollTo(0, 0);
    } else {
      root.style.removeProperty('--app-height');
    }
  };
  vv.addEventListener('resize', sync);
  vv.addEventListener('scroll', sync);
  sync();
}
